import { evaluate } from 'mathjs';

export interface FormulaQuestionAnswer {
  question_key: string;
  answer?: { text?: string | number; guid?: string }[];
}

export interface FormulaCalculationOptions {
  form_id?: string;
  question_key?: string;
  onError?: (details: {
    form_id?: string;
    question_key?: string;
    formula: string;
    error?: string;
  }) => void;
}

const KEY_REGEX = /\b(Q[A-Za-z0-9_]+)\b/g;

function normalizeNumericString(value: string): string {
  const trimmed = value.trim();
  if (trimmed.includes(',') && !trimmed.includes('.')) {
    return trimmed.replace(',', '.');
  }
  return trimmed;
}

function parseNumericAnswer(rawValue: string | number): number | null {
  if (typeof rawValue === 'number') {
    return Number.isFinite(rawValue) ? rawValue : null;
  }
  const normalized = normalizeNumericString(String(rawValue));
  const numericValue = parseFloat(normalized);
  if (Number.isNaN(numericValue)) {
    return null;
  }
  return numericValue;
}

export function extractDependentKeys(formula: string): string[] {
  const matches = formula.match(KEY_REGEX) || [];
  const uniqueKeys: string[] = [];
  matches.forEach((key) => {
    if (!uniqueKeys.includes(key)) {
      uniqueKeys.push(key);
    }
  });
  return uniqueKeys;
}

function getAnswerNumericValue(questions: FormulaQuestionAnswer[], key: string): number | null {
  const matchingQuestion = questions.find((q) => q.question_key === key);
  const rawValue = matchingQuestion?.answer?.[0]?.text;
  if (rawValue === undefined || rawValue === '') {
    return null;
  }
  return parseNumericAnswer(rawValue);
}

export function calculateFormula(
  formula: string | undefined,
  questions: FormulaQuestionAnswer[],
  options?: FormulaCalculationOptions,
): number | null {
  try {
    if (!formula) {
      return null;
    }

    const dependentKeys = extractDependentKeys(formula);

    if (options?.question_key && dependentKeys.includes(options.question_key)) {
      options.onError?.({
        form_id: options.form_id,
        question_key: options.question_key,
        formula,
        error: 'Formula references its own question key',
      });
      return null;
    }

    const scope: Record<string, number> = {};

    for (const key of dependentKeys) {
      const numericValue = getAnswerNumericValue(questions, key);
      if (numericValue === null) {
        return null;
      }
      scope[key] = numericValue;
    }

    const result = evaluate(formula, scope);
    if (typeof result !== 'number' || !Number.isFinite(result)) {
      return null;
    }
    return Number(result.toFixed(4));
  } catch (error) {
    options?.onError?.({
      form_id: options.form_id,
      question_key: options.question_key,
      formula: formula ?? '',
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

export function applyFormulaCalculations<
  T extends FormulaQuestionAnswer & { formula?: string; guid?: string },
>(questions: T[], isRendered: (question: T) => boolean, options?: FormulaCalculationOptions): T[] {
  const formulaQuestionCount = questions.filter((q) => q.formula).length;
  if (formulaQuestionCount === 0) {
    return questions;
  }

  let current = questions;

  for (let pass = 0; pass < formulaQuestionCount; pass++) {
    let changed = false;
    const snapshot = current;
    const next = snapshot.map((question) => {
      if (!question.formula) {
        return question;
      }

      if (!isRendered(question)) {
        if (question.answer && question.answer.length > 0) {
          changed = true;
          return { ...question, answer: [] };
        }
        return question;
      }

      const calculatedValue = calculateFormula(question.formula, snapshot, {
        ...options,
        question_key: question.question_key,
      });

      if (calculatedValue !== null) {
        const text = String(calculatedValue);
        if (question.answer?.[0]?.text === text) {
          return question;
        }
        changed = true;
        return {
          ...question,
          answer: [{ text, guid: question.guid ?? question.question_key }],
        };
      }

      if (question.answer && question.answer.length > 0) {
        changed = true;
        return { ...question, answer: [] };
      }
      return question;
    });

    if (!changed) {
      break;
    }
    current = next;
  }

  return current;
}
